import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import { convertToVwMob } from '../../../Utils/responsive';
import mainLogo from '../../../assets/Logo/main_logo.png';
import adBannerMob from '../../../assets/Icon/project/ad_banner_mob.png';
import engBannerMob from '../../../assets/Icon/project/Eng_Mobile.png';
import backgroundImage from '../../../assets/img/background.png';
import deleteIcon from '../../../assets/Icon/project/delete_icno.png';
import { PATHS } from '../../../routes/paths';
import { usePasswordFind } from '../usePasswordFind';
import {
  MISSIONFUND_PRIVACY_POLICY_URL,
  MISSIONFUND_TERMS_OF_SERVICE_URL,
} from '../../../Utils/missionFundLegalUrls';

const PasswordFindMob = () => {
  const { i18n, t } = useTranslation('translation');
  const navigate = useNavigate();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const {
    email,
    setEmail,
    error,
    isSubmitting,
    isSubmitDisabled,
    isSuccessModalOpen,
    sentEmail,
    closeSuccessModal,
    handleSubmit,
  } = usePasswordFind();
  const hasEmailValue = useMemo(() => Boolean(email.trim()), [email]);
  const shouldShowEmailLabel = isEmailFocused || hasEmailValue;
  const bannerImage = currentLang === 'en' ? engBannerMob : adBannerMob;

  const handleBack = () => {
    closeSuccessModal();
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(PATHS.LOGIN);
  };

  return (
    <MobilePageLayout title="" hideHeader hideFooter showBottomNav={false}>
      <BackgroundOverlay />
      <Container>
        <Card>
          <HeaderRow>
            <LangSelect value={currentLang} onChange={(e) => i18n.changeLanguage(e.target.value)}>
              <option value="ko">{t('lang.ko')}</option>
              <option value="en">{t('lang.en')}</option>
            </LangSelect>
          </HeaderRow>

          <LogoLink to={PATHS.LOGIN}>
            <Logo src={mainLogo} alt="Mission Fund" />
          </LogoLink>

          <Title>{t('page.passwordFind.title')}</Title>
          <Description>
            {t('page.passwordFind.descriptionLine1')}
            <br />
            {t('page.passwordFind.descriptionLine2')}
          </Description>

          <Form onSubmit={handleSubmit}>
            <InputBlock>
              <EmailInputBox $hasError={Boolean(error)} $active={shouldShowEmailLabel}>
                {shouldShowEmailLabel ? (
                  <EmailInputLabel>{t('page.passwordFind.emailPlaceholder')}</EmailInputLabel>
                ) : null}
                <EmailInputRow>
                  <EmailInput
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setIsEmailFocused(true)}
                    onBlur={() => setIsEmailFocused(false)}
                    placeholder={shouldShowEmailLabel ? '' : t('page.passwordFind.emailPlaceholder')}
                    autoComplete="email"
                  />
                  {hasEmailValue ? (
                    <ClearButton
                      type="button"
                      aria-label="clear email"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setEmail('')}
                    >
                      <ClearButtonIcon src={deleteIcon} alt="clear" />
                    </ClearButton>
                  ) : null}
                </EmailInputRow>
              </EmailInputBox>
              {error ? <ErrorText>{error}</ErrorText> : null}
            </InputBlock>

            <SubmitButton type="submit" disabled={isSubmitDisabled}>
              {isSubmitting ? t('page.passwordFind.actions.sending') : t('page.passwordFind.actions.send')}
            </SubmitButton>
          </Form>
          <BannerSection href="https://missionfund.org/support/" target="_blank" rel="noopener noreferrer">
            <BannerImage src={bannerImage} alt={t('page.login.bannerAlt')} />
          </BannerSection>

          <FooterTop>
            <FooterLegalLink
              href={MISSIONFUND_TERMS_OF_SERVICE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.footer.servicePolicy')}
            </FooterLegalLink>
            <FooterDivider>|</FooterDivider>
            <FooterLegalLink
              href={MISSIONFUND_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.footer.privacyPolicy')}
            </FooterLegalLink>
          </FooterTop>
        </Card>
      </Container>
      {isSuccessModalOpen ? (
        <SuccessModalOverlay>
          <SuccessModalCard>
            <SuccessModalTitle>{t('page.passwordFind.successModal.titleMailSent')}</SuccessModalTitle>
            <SuccessModalEmail>{sentEmail}</SuccessModalEmail>
            <SuccessModalDescription>
              {t('page.passwordFind.successModal.descriptionLine1Mob')}
              <br />
              {t('page.passwordFind.successModal.descriptionLine2Mob')}
              <br />
              {t('page.passwordFind.successModal.descriptionLine3Mob')}
              <br />
              {t('page.passwordFind.successModal.descriptionLine4Mob')}
            </SuccessModalDescription>
            <SuccessModalButton type="button" onClick={handleBack}>
              {t('page.passwordFind.successModal.back')}
            </SuccessModalButton>
          </SuccessModalCard>
        </SuccessModalOverlay>
      ) : null}
    </MobilePageLayout>
  );
};

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
  pointer-events: none;
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
`;

const Card = styled.section`
  width: 100%;
  border: none;
  border-radius: 0;
  background: transparent;
  padding: ${convertToVwMob(16)} ${convertToVwMob(20)} ${convertToVwMob(28)};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const LangSelect = styled.select`
  width: ${convertToVwMob(90)};
  height: ${convertToVwMob(34)};
  padding: 0 ${convertToVwMob(28)} 0 ${convertToVwMob(8)};
  border-radius: ${convertToVwMob(4)};
  border: 1px solid #bcbcbc;
  background: #ffffff;
  color: #676767;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(14)};
  font-weight: 500;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23929292' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${convertToVwMob(8)} center;
  background-size: ${convertToVwMob(12)} ${convertToVwMob(12)};
`;

const LogoLink = styled(Link)`
  display: block;
  cursor: pointer;
  text-decoration: none;
`;

const Logo = styled.img`
  width: ${convertToVwMob(170)};
  height: ${convertToVwMob(84)};
  object-fit: contain;
  margin-top: ${convertToVwMob(28)};
  margin-bottom: ${convertToVwMob(44)};
`;

const Title = styled.h1`
  margin: 0 0 ${convertToVwMob(14)};
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 120%;
`;

const Description = styled.p`
  margin: 0;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
`;

const Form = styled.form`
  width: 100%;
  margin-top: ${convertToVwMob(18)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(36)};
`;

const InputBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
`;

const EmailInputBox = styled.div<{ $hasError?: boolean; $active?: boolean }>`
  width: 100%;
  min-height: ${({ $active }) => ($active ? convertToVwMob(74) : convertToVwMob(54))};
  border-radius: ${convertToVwMob(4)};
  border: ${({ $hasError, $active }) =>
    $hasError ? '1.6px solid #f00' : $active ? `1.6px solid #2bc2e1` : '1px solid #eeeeee'};
  background: #ffffff;
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  display: flex;
  flex-direction: column;
  justify-content: ${({ $active }) => ($active ? 'flex-start' : 'center')};
  gap: ${({ $active }) => ($active ? convertToVwMob(2) : '0')};
`;

const EmailInputLabel = styled.label`
  color: #929292;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
`;

const EmailInputRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
`;

const EmailInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  color: #17171b;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(24)};
  font-weight: 400;
  line-height: 150%;
  outline: none;

  &::placeholder {
    color: #929292;
  }
`;

const ClearButton = styled.button`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClearButtonIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(58)};
  padding: ${convertToVwMob(10)} ${convertToVwMob(35.5)};
  border: none;
  border-radius: ${convertToVwMob(12)};
  background: ${({ disabled }) => (disabled ? '#d6d7da' : '#026478')};
  color: ${({ disabled }) => (disabled ? '#BCBCBC' : '#ffffff')};
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
`;

const ErrorText = styled.p`
  width: 100%;
  margin: 0;
  color: #f00;
  text-align: left;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
`;

const BannerSection = styled.a`
  width: 100%;
  margin-top: ${convertToVwMob(104)};
`;

const BannerImage = styled.img`
  width: 100%;
  display: block;
`;

const FooterTop = styled.div`
  margin-top: ${convertToVwMob(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
`;

const FooterLegalLink = styled.a`
  color: #676767;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  line-height: 100%;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    color: #17171b;
    text-decoration: underline;
  }
`;

const FooterDivider = styled.span`
  color: #bcbcbc;
  font-size: ${convertToVwMob(18)};
`;

const SuccessModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(23, 23, 27, 0.42);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${convertToVwMob(20)};
`;

const SuccessModalCard = styled.div`
  display: flex;
  width: min(${convertToVwMob(320)}, calc(100vw - ${convertToVwMob(40)}));
  padding: ${convertToVwMob(30)} ${convertToVwMob(20)} ${convertToVwMob(20)};
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(20)};
  border-radius: ${convertToVwMob(20)};
  background: #fff;
  box-shadow: 0 0 ${convertToVwMob(12)} 0 rgba(0, 0, 0, 0.35);
`;

const SuccessModalTitle = styled.h2`
  margin: 0;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  line-height: 150%;
`;

const SuccessModalEmail = styled.p`
  margin: 0;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  line-height: 150%;
`;

const SuccessModalDescription = styled.p`
  margin: 0;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
`;

const SuccessModalButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(48)};
  padding: ${convertToVwMob(10)};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${convertToVwMob(10)};
  flex: 1 0 0;
  border: none;
  border-radius: ${convertToVwMob(12)};
  background: #026478;
  color: #fff;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
`;

export default PasswordFindMob;

